import React, {FC, memo, useState} from 'react';
import { Menu, Typography } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import i18n from 'i18next';

import { useAppSelector } from 'hooks';

import { ClientStatus, Paths, VerificationStatus } from 'utils/constants';
import Utils from 'utils/utils';

import { ReactComponent as MenuIcon } from 'assets/icons/menu-icon.svg';
import { ReactComponent as InvestIcon } from 'assets/icons/invest.svg';
import { ReactComponent as MyInvestsIcon } from 'assets/icons/my-investments.svg';
import { ReactComponent as StatementIcon } from 'assets/icons/statement.svg';
import { ReactComponent as FundingIcon } from 'assets/icons/deposit.svg';
import {Tooltip} from "antd/es";

const { SubMenu } = Menu;

const menuItems = [
	{
		key: '/account/overview',
		icon: <MenuIcon />,
		label: i18n.t('overviewText'),
	},
	{
		label: i18n.t('investText'),
		key: '/invest',
		icon: <InvestIcon />,
		children: [
			{ label: i18n.t('primaryMarketText'), key: '/account/invest/primarymarket' },
			{
				label: i18n.t('secondaryMarketText'),
				key: '/account/invest/secondarymarket',
			},
		],
	},
	{ key: '/account/my-investments', icon: <MyInvestsIcon />, label: i18n.t('myInvestmentsText') },
	{ key: '/account/statement', icon: <StatementIcon />, label: i18n.t('statementText') },
	{
		key: Paths.FUNDING,
		icon: <FundingIcon />,
		label: `${i18n.t('depositText')} / ${i18n.t('withdrawText')}`,
	},
];

const MenuItem = (props: any) => {
	const { item, onClose, to, params, user, collapsedMenu } = props;
	const [touch, setTouch] = useState(false);
	const [hover, setHover] = useState(false);
	return (
		<NavLink
			role="menuitem"
			onClick={() => {
				if (onClose) {
					onClose();
				}
			}}
			style={{ paddingLeft: '32px' }}
			to={to}
			state={{
				isOpenCalculator:
					item.key === Paths.FUNDING &&
					user.verificationStatus === VerificationStatus.VERIFIED &&
					(!user.riskTolerance.value ||
						!Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2)),
			}}
			key={item.key}
			className={({ isActive }) => {
				return isActive && params.pathname.includes(item.key)
					? 'ant-menu-item ant-menu-item-selected'
					: 'ant-menu-item';
			}}
			onMouseEnter={() => {
				if (!touch && collapsedMenu) {
					setHover(true);
				}
			}}
			onMouseLeave={() => {
				setHover(false);
			}}
			onTouchStart={() => {
				if (collapsedMenu) {
					setTouch(true);
				}
			}}
			onTouchEnd={() => {
				setTimeout(() => {
					setTouch(false);
				}, 500)
			}}
		>
			<Tooltip title={item.label} placement="right" open={hover}>
				{item.icon}
			</Tooltip>
			<span className="ant-menu-title-content">{item.label}</span>
		</NavLink>
	)
}

const MainMenu: FC<{ onClose?: () => void, collapsed?: boolean }> = ({ onClose, collapsed }) => {
	const params = useLocation();

	const user = useAppSelector((state) => state.user.entity);
	const secondaryMarket = useAppSelector((state) => state.user.entity.secondaryMarket);
	const counteroffersCount = secondaryMarket?.counterOffers?.count;

	return (
		<Menu className="sidebar" mode="inline" defaultSelectedKeys={[params.pathname]} role="menu">
			{menuItems.map((item) => {
				if (item.children) {
					return (
						<SubMenu
							key={item.key}
							icon={item.icon}
							title={
								<span className="ant-menu-title-content">
									{item.label}{' '}
									{counteroffersCount > 0 && (
										<Typography.Text type="danger">&nbsp;({counteroffersCount})</Typography.Text>
									)}
								</span>
							}
						>
							{item.children.map((item) => {
								const isSecondaryMarket = item.key === '/account/invest/secondarymarket';
								return (
									<NavLink
										onClick={() => {
											if (onClose) {
												onClose();
											}
										}}
										to={item.key}
										key={item.key}
										className={({ isActive }) => {
											return isActive ? 'ant-menu-item ant-menu-item-selected' : 'ant-menu-item';
										}}
									>
										<span className="ant-menu-title-content">
											{item.label}{' '}
											{isSecondaryMarket && counteroffersCount > 0 && (
												<Typography.Text type="danger">
													&nbsp;({counteroffersCount})
												</Typography.Text>
											)}
										</span>
									</NavLink>
								);
							})}
						</SubMenu>
					);
				} else {
					const isFundingAllow =
						user.status === ClientStatus.ACTIVE &&
						user.verificationStatus === VerificationStatus.VERIFIED &&
						user.taxResidence !== null &&
						!!user.riskTolerance.value &&
						Utils.checkYearsDifferent(user.riskTolerance?.date || '', 2);

					const to =
						item.key === Paths.FUNDING
							? isFundingAllow
								? item.key
								: Utils.fundingRedirect(user)
							: item.key;

					return (
						<MenuItem
							key={item.key}
							user={user}
							to={to}
							params={params}
							onClose={onClose}
							item={item}
							collapsedMenu={collapsed}
						/>
					);
				}
			})}
		</Menu>
	);
};

export default memo(MainMenu);
